import { useId, type ComponentType } from "react";
import type { LucideIcon } from "lucide-react";
import {
  ArchiveX,
  BookOpen,
  Check,
  CircleHelp,
  FileQuestion,
  FolderSearch,
  HardHat,
  FileCheck2,
  FileText,
  Folder,
  Inbox,
  Layers,
  Lock,
  MessageCircle,
  Plus,
  Trash2,
  Workflow,
} from "lucide-react";
import { Link } from "react-router";
import {
  FILE_STATUS_COUNT_WORDS,
  HIDDEN_FILE_STATUSES,
  type BrainCategoryShelf,
  type FileStatus,
  type HiddenFileStatus,
  type KnowledgeSourceType,
  type KnowledgeSummary,
} from "@gb/contracts";
import { GmailMark, GoogleDriveMark, OutlookMark, SharepointMark, WhatsAppMark } from "@/components/gb/brand-marks";
import { cn } from "@/lib/utils";

/**
 * What this project's Brain reads from, above the file list.
 *
 * **No invented numbers.** Roughly half of the design's cards describe ingestion
 * that does not exist — there is no email connector, no WhatsApp, no
 * cross-project register — so those say *In build* and carry no count at all.
 * A plausible "1,240 emails indexed" would make the whole shelf unreadable:
 * once one figure is decoration, a reader cannot trust the ones that are real.
 *
 * The counts that *are* here come from the Brain's summary, which counts each
 * shelf the way its filter lists it, so a shelf and the list it opens cannot
 * disagree.
 */

type Connector = {
  name: string;
  /**
   * The vendor's own mark, not a lucide glyph.
   *
   * A connector row is a row of *products*, and people find the one they want
   * by its logo before they read a word of it. A generic cloud icon standing in
   * for Drive makes the reader parse four labels to find the thing they already
   * recognise. `Local files` keeps an outline folder, because that is what the
   * design has and there is no vendor behind it.
   */
  icon: ComponentType<{ className?: string }>;
  /** Live state, or null when the thing does not exist yet. */
  state: "connected" | "available" | null;
  onAdd?: () => void;
  addLabel?: string;
  /** A second way to add, beside the first: a whole folder, for local files (2026-10-04). */
  addAlso?: { label: string; onClick: () => void };
  disabled?: boolean;
  tooltip?: string;
  /** No remote account behind it, so "Connected" would be meaningless. */
  local?: boolean;
  /** What the card says of its state, where the default words would not be true. */
  stateLabel?: string;
};

export function Connectors({
  drive,
  onDrive,
  sharepoint,
  onSharepoint,
  outlook,
  onOutlook,
  onLocal,
  onLocalFolder,
}: {
  drive: { connected: boolean; configured: boolean; busy: boolean };
  onDrive: () => void;
  /**
   * The project's SharePoint folder (`plans/sharepoint-sync.md`). `blocked`
   * is why it cannot be linked here, in one sentence: the deployment or the
   * organisation is not set up yet.
   */
  sharepoint: { linked: boolean; blocked: string | null };
  onSharepoint: () => void;
  /**
   * The project's Outlook folders (`plans/outlook-sync.md`): how many sync
   * into it, and why none can be chosen here, when that is so.
   */
  outlook: { syncing: number; blocked: string | null };
  onOutlook: () => void;
  onLocal: () => void;
  /** Pick a whole folder: its files, each with the folder it was in. */
  onLocalFolder: () => void;
}) {
  const connectors: Connector[] = [
    // Gmail is in the design and there is no email ingestion anywhere in this
    // system — not a connector, not a parser, not a source kind. It is a
    // placeholder that says so.
    { name: "Gmail", icon: GmailMark, state: null },
    {
      name: "Google Drive",
      icon: GoogleDriveMark,
      state: drive.connected ? "connected" : drive.configured ? "available" : null,
      onAdd: onDrive,
      addLabel: drive.connected ? "Add more" : "Connect",
      disabled: drive.busy,
      tooltip: drive.configured ? undefined : "Google Drive isn’t configured for this environment.",
    },
    {
      name: "SharePoint",
      icon: SharepointMark,
      // A linked folder syncs; an unlinked one waits for a person to link it.
      // When the deployment or the organisation is not set up, the card says
      // so, not "Coming soon": the feature exists.
      // A linked folder whose organisation is not connected does not sync, and
      // the card says so (2026-10-18, QA finding 4).
      // "Available", not null, keeps "Manage" on a linked card.
      state: sharepoint.linked ? (sharepoint.blocked ? "available" : "connected") : sharepoint.blocked ? null : "available",
      stateLabel: sharepoint.blocked
        ? sharepoint.linked
          ? "Not syncing"
          : "Not set up yet"
        : sharepoint.linked
          ? "Syncing a folder"
          : "No folder linked",
      onAdd: onSharepoint,
      addLabel: sharepoint.linked ? "Manage" : "Link a folder",
      tooltip: sharepoint.blocked ?? undefined,
    },
    {
      name: "Outlook",
      icon: OutlookMark,
      // Folders people chose from their own mailboxes sync; with none, any
      // person on the project can choose some. Folders whose organisation is
      // not connected do not sync, and the card says so (2026-10-18, QA
      // finding 4).
      // "Available", not null, keeps "Manage" on a card with folders.
      state: outlook.syncing > 0 ? (outlook.blocked ? "available" : "connected") : outlook.blocked ? null : "available",
      stateLabel: outlook.blocked
        ? outlook.syncing > 0
          ? "Not syncing"
          : "Not set up yet"
        : outlook.syncing > 0
          ? outlook.syncing === 1
            ? "Syncing 1 folder"
            : `Syncing ${outlook.syncing} folders`
          : "Sync folders from your mailbox",
      onAdd: onOutlook,
      addLabel: outlook.syncing > 0 ? "Manage" : "Choose folders",
      tooltip: outlook.blocked ?? undefined,
    },
    { name: "WhatsApp", icon: WhatsAppMark, state: null },
    {
      name: "Local files",
      icon: Folder,
      state: "connected",
      local: true,
      onAdd: onLocal,
      addLabel: "Add files",
      addAlso: { label: "Add a folder", onClick: onLocalFolder },
    },
  ];

  return (
    <section className="mb-6">
      <h2 className="mb-2.5 font-display text-[0.66rem] font-semibold tracking-[0.12em] text-muted uppercase">
        Connectors
      </h2>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
        {connectors.map((connector) => (
          <ConnectorCard key={connector.name} connector={connector} />
        ))}
        <div
          title="More connectors are coming."
          className="flex flex-col justify-center rounded-card-lg border-[1.5px] border-dashed border-border px-4 py-4 text-muted/70"
        >
          <Plus size={20} aria-hidden />
          <span className="mt-2 font-display text-[0.92rem] font-semibold">Add more</span>
          <span className="text-[0.82rem]">Coming soon</span>
        </div>
      </div>
    </section>
  );
}

function ConnectorCard({ connector }: { connector: Connector }) {
  const live = connector.state !== null;
  const Icon = connector.icon;

  return (
    <div
      title={connector.tooltip}
      className={cn(
        "flex flex-col rounded-card-lg border-[1.5px] px-4 py-4",
        live ? "border-border bg-surface" : "border-border bg-subtle/60",
      )}
    >
      {/* Muted rather than desaturated when the connector is not live. A grey
          logo is a logo nobody recognises, and recognition is the only job it
          has. */}
      <Icon className={cn("size-5", live ? undefined : "opacity-40")} />
      <span
        className={cn(
          "mt-2 font-display text-[0.92rem] font-semibold",
          live ? "text-ink" : "text-muted/70",
        )}
      >
        {connector.name}
      </span>
      <span
        className={cn(
          "text-[0.82rem]",
          connector.state === "connected" ? "font-semibold text-ok" : "text-muted",
        )}
      >
        {/* "Connected" is a claim about a *remote account*. Local files are
            not connected to anything — they are the absence of a connector —
            and saying so invited the reader to wonder what they had linked. */}
        {connector.stateLabel ??
          (connector.state === "connected"
            ? connector.local
              ? "Upload from this device"
              : "Connected"
            : connector.state === "available"
              ? "Not connected"
              : "Coming soon")}
      </span>
      {connector.onAdd && connector.state !== null && (
        <div className="mt-3 flex flex-wrap gap-2">
          <AddButton disabled={connector.disabled} onClick={connector.onAdd} label={connector.addLabel} />
          {connector.addAlso && (
            <AddButton
              disabled={connector.disabled}
              onClick={connector.addAlso.onClick}
              label={connector.addAlso.label}
            />
          )}
        </div>
      )}
    </div>
  );
}

function AddButton({ disabled, onClick, label }: { disabled?: boolean; onClick: () => void; label?: string }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "w-fit cursor-pointer rounded-input border-[1.5px] border-border bg-surface px-3 py-1",
        "font-display text-[0.7rem] font-semibold tracking-[0.06em] uppercase text-ink",
        "transition-colors hover:border-brand/60 disabled:opacity-50",
      )}
    >
      {label}
    </button>
  );
}

/**
 * The list filter a shelf *is*, one-to-one: one source type, one category,
 * the files not sorted yet, the files waiting for a decision, one file status,
 * or the files not organised yet.
 *
 * Wider than a source type because the project's documents are shelved by
 * the tenant's categories (`document-ai/0032`, 2026-10-03), and every
 * category is a filter of its own. `category` is a category's key: the files
 * whose main category it is. The shelf's "Also relevant here" group is a
 * second list the dialog asks for itself, not a shelf. `sorted: "no"` is the
 * files with no main category. `decision: "open"` is the files the model was
 * not sure how to place (`document-ai/0016` §11). `status` is the files the
 * check gave that status, and the ones it asks about with it (2026-09-26); it
 * is the only filter that lists a file the agent may not read
 * (`document-ai/0013`). `organised: "no"` is the files an organise step has
 * not done yet (2026-09-26, RET-12).
 */
type NoFilter = {
  sourceType?: undefined;
  category?: undefined;
  sorted?: undefined;
  decision?: undefined;
  status?: undefined;
  organised?: undefined;
  removed?: undefined;
};
export type ShelfFilter =
  | (Omit<NoFilter, "sourceType"> & { sourceType: KnowledgeSourceType })
  | (Omit<NoFilter, "category"> & { category: string })
  | (Omit<NoFilter, "sorted"> & { sorted: "no" })
  | (Omit<NoFilter, "decision"> & { decision: "open" })
  | (Omit<NoFilter, "status"> & { status: FileStatus })
  | (Omit<NoFilter, "organised"> & { organised: "no" })
  | (Omit<NoFilter, "removed"> & { removed: "yes" });

function sameFilter(a: ShelfFilter, b: ShelfFilter): boolean {
  // Every field of the union, or two different filters compare equal and the
  // wrong shelf reads as pressed.
  return (
    a.sourceType === b.sourceType &&
    a.category === b.category &&
    a.sorted === b.sorted &&
    a.decision === b.decision &&
    a.status === b.status &&
    a.organised === b.organised &&
    a.removed === b.removed
  );
}

/**
 * The shelf that asks about each hidden status (2026-09-26, tracker HID-11,
 * the default the owner can change). Until then only another project's files
 * had one, and a wrong template, private or junk hide was on no shelf at all.
 * In the Austrian set the one private hide was a work-travel receipt, and an answer then
 * said "No".
 */
const HIDE_SHELVES: Record<HiddenFileStatus, { name: string; icon: LucideIcon; description: string }> = {
  "other-project": {
    name: "Looks like another project",
    icon: FolderSearch,
    description: "Files that name a different project. Keep or remove each one.",
  },
  template: {
    name: "Looks like a template",
    icon: FileQuestion,
    description: "Blank forms and files made for another project as a start. Keep or remove each one.",
  },
  private: {
    name: "Looks private",
    icon: Lock,
    description: "Files that are not project work, such as a private receipt. Keep or remove each one.",
  },
  junk: {
    name: "Looks like junk",
    icon: Trash2,
    description: "Files with nothing of use in them. Keep or remove each one.",
  },
};

type Shelf = {
  /** Unique on the page. Not the name: a tenant's category could be called "Notes". */
  id: string;
  name: string;
  icon: LucideIcon;
  description: string | null;
  /**
   * Whether this shelf can hold anything at all.
   *
   * Separate from the count, and the separation is the whole point: **empty and
   * impossible are different facts.** A project with no documents uploaded yet
   * and a shelf with no ingestion behind it both show zero, and they send a
   * reader to completely different places — one to the upload button, one to
   * wait. Deriving the state from `count === 0` collapses them, which is the
   * mistake this type exists to stop.
   */
  available: boolean;
  count: number;
  /**
   * A category's files that live on another shelf and list this one: its
   * folded "Also relevant here" group (`document-ai/0032`). Not in `count`,
   * so the counts add up to the files. A shelf with only these still opens,
   * since its group is worth reading, but it does not hold anything of its
   * own, so it is not shown as done.
   */
  alsoRelevant?: number;
  /**
   * Singular and plural, spelled out.
   *
   * Not `unit + "s"` — that gives "5 entrys". English plurals are not a rule
   * worth deriving for six fixed strings.
   */
  unit: [one: string, many: string];
  /** What the count says of them: "indexed", unless the shelf is a to-do. */
  verb?: string;
  /**
   * The shelf asks something of the reader, so it is not shown as done: no
   * green tick, and the count in the warning colour.
   */
  attention?: boolean;
  /**
   * Where this shelf goes instead of filtering the list.
   *
   * Set only for **crew**, which is real citable evidence and is *not* a
   * `knowledge_base` row — it is computed live from the roster, so there is no
   * source type to filter by and the records live on their own screen. Every
   * other shelf filters in place.
   */
  href?: string;
  /**
   * The list filter this shelf *is*, one-to-one.
   *
   * A shelf that spanned two source types could not be clicked without lying
   * about where it landed you — which is why "Notes & decisions" is two cards.
   * The filter takes one type or one category; a card that maps to one of them
   * is a card whose click is honest.
   */
  filter: ShelfFilter;
};

export function Shelves({
  projectId,
  counts,
  categories,
  notSorted,
  unorganisedDocuments,
  removedAtSource = 0,
  needsDecision,
  hidden,
  crewCount,
  active,
  onSelect,
}: {
  projectId: string;
  counts: Partial<Record<KnowledgeSourceType, number>>;
  /**
   * Every one of the tenant's categories, in its order, empty ones included:
   * the Brain summary's `categories` (`document-ai/0032`).
   */
  categories: readonly BrainCategoryShelf[];
  /** Files with no main category: the summary's `notSorted`. */
  notSorted: number;
  /**
   * Files an organise step has not done yet: not checked, not placed among
   * their versions, or with no links (2026-09-26, RET-12).
   */
  unorganisedDocuments: number;
  /** Files removed at their source, SharePoint or Outlook: kept, out of answers (2026-10-18). */
  removedAtSource?: number;
  /** Files with a question waiting for a person. */
  needsDecision: number;
  /**
   * Files the agent may not read, counted per status (`document-ai/0013`),
   * and the files the check asks about, per status it would have set. Two
   * things come from it: the shelf that asks about each hidden status, and the
   * line under the shelves saying how many are hidden.
   */
  hidden: KnowledgeSummary["hidden"];
  /** People on this site. Null while the roster is still loading. */
  crewCount: number | null;
  active: ShelfFilter | undefined;
  /** The shelf's own name rides along: "Decisions" reads better as a dialog
   *  title than the source type's label, which is the singular "Decision". */
  onSelect: (filter: ShelfFilter | undefined, name: string) => void;
}) {
  // **One shelf per category of the tenant's list** (`document-ai/0032`,
  // 2026-10-03), where there was one per area of the project's own outline.
  // Every project of the tenant has the same shelves, so a person knows where
  // a file lands. Names and descriptions are the tenant's, in its language.
  //
  // **Two groups, each in the tenant's order:** the categories that hold a
  // file whose main category they are, then the ones that hold none. A file
  // that only lists a category does not move it into the first group: the
  // counts are of main files, so they add up to the files.
  const categoryShelf = (category: BrainCategoryShelf): Shelf => ({
    id: `category:${category.key}`,
    name: category.name,
    icon: FileCheck2,
    description: category.description,
    available: true,
    count: category.documents,
    alsoRelevant: category.alsoRelevant,
    unit: ["document", "documents"],
    filter: { category: category.key },
  });
  const withFiles = categories.filter((category) => category.documents > 0).map(categoryShelf);
  const noFilesYet = categories.filter((category) => category.documents === 0).map(categoryShelf);
  // The files no category holds, last of the files: their card is not
  // written yet, or could not be. Every file is then on exactly one shelf of
  // the first group. It replaces the outline's "Other". Not an organise gap:
  // no organise run sorts a file, the card does.
  if (notSorted > 0) {
    withFiles.push({
      id: "sorted:no",
      name: "Not sorted yet",
      icon: Inbox,
      description:
        "Files in no category yet: still being read, or their card could not be written. A person can move each one from its page.",
      available: true,
      count: notSorted,
      unit: ["document", "documents"],
      filter: { sorted: "no" },
    });
  }

  // First, because these are the shelves that ask something of the reader
  // (decided by the product owner 2026-09-22, `document-ai/0016` §11 and
  // `document-ai/0013`). Only while there is something to answer: an empty
  // to-do is not a shelf.
  //
  // Every question since 2026-09-26 (RET-04), not only a placement: a file
  // the check asked about, and a link the model was not sure of. The
  // project's own organise run writes these with no import, so no import
  // review shows them. The default, which the owner can change.
  const asking: Shelf[] = [];
  if (needsDecision > 0) {
    asking.push({
      id: "decision:open",
      name: "Needs a decision",
      icon: CircleHelp,
      description:
        "Files with a question: where a file sits among its versions, whether it belongs to this project, or a link to another file. Open one to answer.",
      available: true,
      count: needsDecision,
      unit: ["document", "documents"],
      verb: "waiting",
      attention: true,
      filter: { decision: "open" },
    });
  }
  // The files the check hid, one shelf per status. They are in no ordinary
  // list and in no search, so these shelves are the only way to them, and a
  // person keeps or removes each file on it (`document-ai/0013`).
  //
  // **Every hidden status asks** since 2026-09-26 (HID-11, the default the
  // owner can change), not only another project's: "a template nobody asked
  // about is not a to-do" left a wrong template, private or junk hide on no
  // shelf at all. Each shelf also lists, first, the files the check would
  // have hidden with that status and asked about instead: they are still used
  // in answers, so a wrong one there matters most.
  for (const status of HIDDEN_FILE_STATUSES) {
    const count = (hidden.byStatus[status] ?? 0) + (hidden.asked[status] ?? 0);
    if (count === 0) continue;
    const shelf = HIDE_SHELVES[status];
    asking.push({
      id: `status:${status}`,
      name: shelf.name,
      icon: shelf.icon,
      description: shelf.description,
      available: true,
      count,
      unit: ["document", "documents"],
      verb: "waiting",
      attention: true,
      filter: { status },
    });
  }
  // A file that an organise step has not done yet: not checked, not placed
  // among its versions, or with no links. Since 2026-09-26 (RET-12) every
  // gap, not only the outline's: in the Austrian set 21 of 21 files had no
  // links, and the shelf counted none of them. Beside the shelves that ask
  // since 2026-10-03, not among the categories: it cuts across them, and its
  // count is not one of the files' places (`document-ai/0032`). It asks
  // nothing itself; "Organise now" is the line at the top.
  const unorganised: Shelf[] = [];
  if (unorganisedDocuments > 0) {
    unorganised.push({
      id: "organised:no",
      name: "Not organised yet",
      icon: Workflow,
      description: "Files that are not checked, not placed among their versions, or with no links yet.",
      available: true,
      count: unorganisedDocuments,
      unit: ["document", "documents"],
      filter: { organised: "no" },
    });
  }
  // Files removed at their source, SharePoint or Outlook, and what was
  // unpacked from them: kept, and out of answers. Shown only when there are
  // some, so a person can still find one (the owner, 2026-10-18, QA finding
  // 9). Each row says where it was removed.
  if (removedAtSource > 0) {
    unorganised.push({
      id: "removed:yes",
      name: "Removed at source",
      icon: ArchiveX,
      description: "Removed or moved out of their SharePoint or Outlook folder. Kept here, and not used in answers.",
      available: true,
      count: removedAtSource,
      unit: ["document", "documents"],
      filter: { removed: "yes" },
    });
  }

  const otherSources: Shelf[] = [
    {
      id: "manual",
      name: "Equipment & supplier manuals",
      icon: BookOpen,
      // Deliberately not "pulled from vendor portals", as the design has it.
      // Nothing pulls them; they are seeded. Saying otherwise describes an
      // integration that does not exist.
      //
      // Only the seeded manuals, counted in sections because that is what each
      // row is. An uploaded manual is a document like any other, on the shelf
      // of its category (`document-ai/0032`).
      description: "Vendor product manuals for the equipment on this site.",
      available: true,
      count: counts.manual ?? 0,
      unit: ["section", "sections"],
      filter: { sourceType: "manual" },
    },
    // **Site journal is deliberately not a shelf here.**
    //
    // The Journal has its own screen, which is where anyone looking for a day
    // record goes — and it shows the day, its notes, its deliveries and its
    // photographs together. A second door into the same records, listing them
    // as flat Brain entries stripped of that structure, is a worse view of the
    // same thing and invites the reader to wonder which one is authoritative.
    //
    // The agent reads the journal structurally and cites it by its own link
    // kinds; only seeded days have a Brain row. What goes is one navigation card.
    {
      id: "upload",
      name: "Notes",
      icon: FileText,
      description: "Written straight into the Brain, by hand.",
      available: true,
      count: counts.upload ?? 0,
      unit: ["note", "notes"],
      filter: { sourceType: "upload" },
    },
    {
      id: "decision",
      name: "Decisions",
      icon: Layers,
      description: "Captured from email, WhatsApp and journals, or accepted from an answer.",
      available: true,
      count: counts.decision ?? 0,
      unit: ["decision", "decisions"],
      filter: { sourceType: "decision" },
    },
    {
      id: "crew",
      name: "Crew",
      icon: HardHat,
      description:
        "Who is on site, their certificates and what each one is cleared to do.",
      available: true,
      // Computed from the roster on every question rather than stored, so it
      // is never stale — and so it has no `knowledge_base` row to count.
      count: crewCount ?? 0,
      unit: ["person", "people"],
      filter: { sourceType: "other" }, // never sent: a linked shelf does not filter
      href: `/projects/${projectId}/crew`,
    },
    {
      id: "whatsapp",
      name: "WhatsApp",
      icon: MessageCircle,
      description: "Site crew group and supplier chats.",
      available: false,
      count: 0,
      unit: ["message", "messages"],
      filter: { sourceType: "other" }, // never sent: the shelf never opens
    },
  ];

  const select = (shelf: Shelf, isActive: boolean) =>
    // Clicking the shelf you are already filtered to clears it, so the card is
    // a toggle rather than a one-way trip.
    onSelect(isActive ? undefined : shelf.filter, shelf.name);

  return (
    <section className="mb-6">
      <h2 className="mb-2.5 font-display text-[0.66rem] font-semibold tracking-[0.12em] text-muted uppercase">
        Shelves
      </h2>
      {/* The shelves that ask, then the categories in their two groups, then
          what is not a project file. A group with no shelf says nothing: an
          empty heading reads as something that failed to load. */}
      <div className="flex flex-col gap-4">
        <ShelfGrid shelves={[...asking, ...unorganised]} active={active} onSelect={select} />
        <ShelfGroup title="With files" shelves={withFiles} active={active} onSelect={select} />
        <ShelfGroup title="No files yet" shelves={noFilesYet} active={active} onSelect={select} />
        <ShelfGroup title="Other sources" shelves={otherSources} active={active} onSelect={select} />
      </div>
      <HiddenLine hidden={hidden} />
    </section>
  );
}

/** A labelled run of shelves: "With files", "No files yet", "Other sources". */
function ShelfGroup({
  title,
  shelves,
  active,
  onSelect,
}: {
  title: string;
  shelves: readonly Shelf[];
  active: ShelfFilter | undefined;
  onSelect: (shelf: Shelf, isActive: boolean) => void;
}) {
  const id = useId();
  if (shelves.length === 0) return null;
  return (
    // A labelled group, not a heading level of its own: the shelf names are
    // the headings a reader moves between, as they were before the groups.
    <div role="group" aria-labelledby={id}>
      <p id={id} className="mb-2 text-[0.8rem] font-semibold text-ink-slate">
        {title}
      </p>
      <ShelfGrid shelves={shelves} active={active} onSelect={onSelect} />
    </div>
  );
}

function ShelfGrid({
  shelves,
  active,
  onSelect,
}: {
  shelves: readonly Shelf[];
  active: ShelfFilter | undefined;
  onSelect: (shelf: Shelf, isActive: boolean) => void;
}) {
  if (shelves.length === 0) return null;
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {shelves.map((shelf) => {
        const isActive = !shelf.href && active !== undefined && sameFilter(active, shelf.filter);
        return (
          <ShelfCard
            key={shelf.id}
            shelf={shelf}
            active={isActive}
            onSelect={() => onSelect(shelf, isActive)}
          />
        );
      })}
    </div>
  );
}

/**
 * What the agent may not read, under the shelves.
 *
 * **A fact, not a to-do.** Muted, with none of the warning colours the asking
 * shelves use: a project whose import carried 61 blank templates is not 61
 * problems, and painting it amber would make the two shelves that *do* want an
 * answer indistinguishable from the ones that do not (`data-model/0013` §8).
 *
 * Largest first, because that is the one a reader wants named. Statuses the
 * summary did not count are not printed as zeros.
 */
function HiddenLine({ hidden }: { hidden: KnowledgeSummary["hidden"] }) {
  if (hidden.total <= 0) return null;

  const parts = HIDDEN_FILE_STATUSES.map((status) => ({
    status,
    count: hidden.byStatus[status] ?? 0,
  }))
    .filter((part) => part.count > 0)
    // Stable on the source order, so a tie reads in the statuses' own order.
    .sort((a, b) => b.count - a.count)
    .map(({ status, count }) => {
      // In the contract since 2026-09-25, so the selector's line counts in
      // the same words: "17 templates", not "17 template".
      const words = FILE_STATUS_COUNT_WORDS[status];
      return `${count} ${words[count === 1 ? 0 : 1]}`;
    });

  const total = `${hidden.total} ${hidden.total === 1 ? "file" : "files"} hidden from answers`;

  return (
    <p className="mt-2.5 text-[0.82rem] leading-[1.5] text-muted">
      {parts.length > 0 ? `${total}: ${parts.join(", ")}.` : `${total}.`}
    </p>
  );
}

function ShelfCard({
  shelf,
  active,
  onSelect,
}: {
  shelf: Shelf;
  active: boolean;
  onSelect: () => void;
}) {
  const live = shelf.available && shelf.count > 0;
  // A category with no file of its own still opens when files elsewhere list
  // it (`document-ai/0032`): its "Also relevant here" group is the reason to
  // look. It stays drawn as holding nothing, since it holds nothing.
  const opens = live || (shelf.available && (shelf.alsoRelevant ?? 0) > 0);

  // Three shapes, written out rather than made polymorphic. A shelf that lives
  // on another screen is a link, one that opens its contents here is a button,
  // and one with nothing in it is inert markup — and the props for those are
  // genuinely different types.
  const body = <ShelfBody shelf={shelf} live={live} />;
  const shell = cn(
    "flex gap-3.5 rounded-card-lg border-[1.5px] px-5 py-4 text-left",
    live
      ? cn("bg-surface", shelf.attention ? "border-warn/40" : "border-ok/30")
      : opens
        ? "border-border bg-surface"
        : "border-border bg-subtle/60",
    opens && "cursor-pointer transition-colors hover:border-brand/50",
    active && "border-brand ring-1 ring-brand/40",
  );

  if (!opens) return <article className={shell}>{body}</article>;
  if (shelf.href) {
    return (
      <Link to={shelf.href} className={shell}>
        {body}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onSelect} aria-pressed={active} className={shell}>
      {body}
    </button>
  );
}

function ShelfBody({ shelf, live }: { shelf: Shelf; live: boolean }) {
  const Icon = shelf.icon;
  return (
    <>
      <span
        className={cn(
          "flex size-9 shrink-0 items-center justify-center rounded-tile",
          !live
            ? "bg-border/40 text-muted/70"
            : shelf.attention
              ? "bg-warn-bg text-warn"
              : "bg-ok-bg text-ok",
        )}
      >
        <Icon size={18} aria-hidden />
      </span>
      <div className="min-w-0">
        <h3
          className={cn(
            "flex items-center gap-1.5 font-display text-[0.98rem] font-semibold",
            live ? "text-ink" : "text-muted/80",
          )}
        >
          {shelf.name}
          {live && !shelf.attention && <Check size={14} aria-hidden className="text-ok" />}
        </h3>
        {shelf.description && (
          <p className="mt-0.5 text-[0.86rem] leading-relaxed text-muted">{shelf.description}</p>
        )}
        <p
          className={cn(
            "mt-2 text-[0.84rem] font-semibold",
            !live ? "text-muted/70" : shelf.attention ? "text-warn-text" : "text-ok",
          )}
        >
          {/* Three states, not two. "In build" means no ingestion exists;
              "Nothing indexed yet" means it does and this project has not used
              it — which is the difference between waiting and uploading. */}
          {!shelf.available
            ? "In build — available soon"
            : shelf.count === 0
              ? "Nothing indexed yet"
              : `${shelf.count} ${shelf.unit[shelf.count === 1 ? 0 : 1]} ${shelf.verb ?? "indexed"}`}
          {/* Said apart from the count, which is of main files only. */}
          {shelf.available && (shelf.alsoRelevant ?? 0) > 0 && (
            <span className="font-normal text-muted"> · {shelf.alsoRelevant} also relevant</span>
          )}
        </p>
      </div>
    </>
  );
}
